import {
  UPGRADE_CRAFT_LOAD_MULTIPLIER,
  getSonarPulsePowerMw
} from '../shared/constants.js';
import type {
  ControlInput,
  GameSnapshot,
  Role,
  RoomState,
  SonarScanType,
  SubmarineState
} from '../shared/types.js';
import { state as controllerState } from './controllerState.js';
import type { ControllerNetwork } from './controllerNetwork.js';
import { openTechTreePanel, showQuickToast } from './controllerPanels.js';
import { createCard, clamp } from './controllerUiHelpers.js';
import { type SonarPingHit } from './rendering/SonarScanView.js';
import { PeriscopeCameraView } from './rendering/PeriscopeCameraView.js';
import { BottomHullCameraView } from './rendering/BottomHullCameraView.js';
import { DEFAULT_RENDER_TUNING, type RenderTuning } from './rendering/renderTuning.js';
import { submarineToMapPoint, SPATIAL_SCALE } from '../shared/spatial.js';
import type { TileJson } from '../shared/maps/tileJson.js';
import { buildNavData, computeTileOrigins, tileKeyForGlb, tileKeyForSlot } from '../shared/maps/navData.js';
import {
  VISUAL_DISC_GRID_METERS, applyReveal, buildRevealGraph, computeScanReveal, computeVisualReveal,
  type Point3, type RevealGraph, type ScanType
} from '../shared/maps/navReveal.js';
import { getSourceGlbTileGridOffset } from '../shared/maps/tiledGlb.js';
import { verticalGap } from '../shared/maps/tileStreaming.js';
import type { NavGatewayEntry } from './rendering/navSection.js';
import { DEFAULT_GRAPHICS_QUALITY, type GraphicsQuality } from './rendering/quality.js';
import {
  createHarpoonRoomController,
  type HarpoonRoomController
} from './rooms/HarpoonRoomController.js';
import { createWeaponsRoomController, type WeaponsRoomController } from './rooms/WeaponsRoomController.js';
import { createEngineeringRoomController } from './rooms/EngineeringRoomController.js';
import { createPilotRoomController } from './rooms/PilotRoomController.js';
import { createSonarRoomController } from './rooms/SonarRoomController.js';
import { createCaptainRoomController } from './rooms/CaptainRoomController.js';
import { createDockingRoomController } from './rooms/DockingRoomController.js';
import { createBatteryRoomController } from './rooms/BatteryRoomController.js';
import { createEngineRoomController } from './rooms/EngineRoomController.js';
import { createLifeSupportRoomController , formatLifeSupportConsoleOutput} from './rooms/LifeSupportRoomController.js';
import { createDroneRoomController } from './rooms/DroneRoomController.js';
import { createNavigationRoomController } from './rooms/NavigationRoomController.js';
import { createCommsRoomController } from './rooms/CommsRoomController.js';
import { createPeriscopeRoomController } from './rooms/PeriscopeRoomController.js';
import { createBallastRoomController } from './rooms/BallastRoomController.js';
import { createStorageRoomController } from './rooms/StorageRoomController.js';
import { createSavedGameState, downloadSavedGame } from './savedGameExport.js';

const controls = document.querySelector<HTMLDivElement>('#controls');
const ctrlZoneLeftTop = document.querySelector<HTMLDivElement>('#ctrlZoneLeftTop');
const ctrlZoneLeftBottom = document.querySelector<HTMLDivElement>('#ctrlZoneLeftBottom');
const ctrlZoneRightAux = document.querySelector<HTMLDivElement>('#ctrlZoneRightAux');
const controllerTechTreePanel = document.querySelector<HTMLElement>('#controllerTechTreePanel');
const controllerTechTreeBody = document.querySelector<HTMLElement>('#controllerTechTreeBody');
const controllerQuickToast = document.querySelector<HTMLElement>('#controllerQuickToast');

const showControllerQuickToast = (message: string): void => showQuickToast(controllerQuickToast, message);

/**
 * Saves the game on the server (tagged with the signed-in players aboard) and tells the player
 * what happened. If the server can't take it, falls back to downloading a save file.
 */
export const saveGameWithFeedback = (getRevealedNodes: () => Set<string>): void => {
  if (!controllerState.lastRoomState || !controllerState.lastSnapshot) {
    showControllerQuickToast('Save failed: not connected to a game yet.');
    return;
  }
  if (!controllerState.lastSnapshot.submarine.docked) {
    showControllerQuickToast('Save is only available while docked.');
    return;
  }
  const savedGame = createSavedGameState({ getRevealedNodes });
  if (!savedGame) {
    showControllerQuickToast('Save failed: could not capture game state.');
    return;
  }

  const savedBy = controllerState.lastRoomState.players?.find((p) => p.id === controllerState.currentPlayerId)?.name ?? null;
  showControllerQuickToast('Saving…');
  void fetch('/api/games/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roomCode: controllerState.currentRoomCode, savedBy, savedGame })
  }).then(async (response) => {
    if (response.ok) {
      showControllerQuickToast('✓ Game saved to the server');
      return;
    }
    const message = (await response.json().catch(() => null) as { message?: string } | null)?.message;
    throw new Error(message ?? `${response.status} ${response.statusText}`);
  }).catch((error) => {
    try {
      const fileName = downloadSavedGame(savedGame);
      showControllerQuickToast(`Server save failed (${error instanceof Error ? error.message : 'error'}); downloaded ${fileName}`);
    } catch {
      showControllerQuickToast('Save failed.');
    }
  });
};

let controllerNetwork: ControllerNetwork | null = null;
let emitControlFn: ((input: ControlInput) => void) | null = null;
let graphicsQuality: GraphicsQuality = DEFAULT_GRAPHICS_QUALITY;
let controllerRenderTuning: RenderTuning = DEFAULT_RENDER_TUNING;
let userFpsOverride: number | undefined = undefined;

export const setControllerNetwork = (value: ControllerNetwork | null): void => {
  controllerNetwork = value;
};

export const setControllerEmit = (fn: (input: ControlInput) => void): void => {
  emitControlFn = fn;
};

export const setGraphicsQuality = (value: GraphicsQuality): void => {
  graphicsQuality = value;
};

export const setUserFpsOverride = (fps: number | undefined): void => {
  userFpsOverride = fps;
};

export const setControllerRenderTuning = (value: RenderTuning): void => {
  controllerRenderTuning = value;
  harpoonRoomController.setRenderTuning(controllerRenderTuning);
  weaponsRoomController.setRenderTuning(controllerRenderTuning);
  droneRoomController.setRenderTuning(controllerRenderTuning);
  periscopeRoomController.setRenderTuning(controllerRenderTuning);
  bottomHullCameraView?.setRenderTuning(controllerRenderTuning);
};

export const setControllerSnapshot = (snapshot: GameSnapshot): void => {
  harpoonRoomController.setSnapshot(snapshot);
  weaponsRoomController.setSnapshot(snapshot);
  droneRoomController.setSnapshot(snapshot);
  periscopeRoomController.setSnapshot(snapshot);
  navigationRoomController.setSnapshot(snapshot);
  commsRoomController.setSnapshot(snapshot);
  checkVisualRevealForSnapshot(snapshot);
  applyNavRoutes(snapshot);
};

/** A data log was recovered (EVENTS.logUnlocked): pulse the captain's Log tab. */
export const notifyCaptainLogUnlocked = (log: import('../shared/narrative.js').LogEntry): void => captainRoomController.notifyLogUnlocked(log);

export const setControllerRoomState = (state: RoomState): void => {
  harpoonRoomController.setRoomState(state);
  weaponsRoomController.setRoomState(state);
  droneRoomController.setRoomState(state);
  periscopeRoomController.setRoomState(state);
  navigationRoomController.setRoomState(state);
  storageRoomController.updateUi();
};

// Lightweight refresh for the storage room's live UI (queue count, craft bars).
// Unlike setControllerRoomState this runs on every room-state broadcast, not just
// on mission changes, so the crafting screen and queue button stay in sync.
export const updateStorageRoomState = (): void => {
  storageRoomController.updateUi();
};

const emitControl = (input: ControlInput): void => {
  emitControlFn?.(input);
};

let getActiveMap: (() => import('../shared/maps/types.js').MapDefinition | null) | null = null;
let getRoleMeta: ((role: Role) => { label: string; icon: string }) | null = null;

export const setActiveMapGetter = (getter: () => import('../shared/maps/types.js').MapDefinition | null): void => {
  getActiveMap = getter;
};

export const setRoleMetaGetter = (getter: (role: Role) => { label: string; icon: string }): void => {
  getRoleMeta = getter;
};

export const resolveRoleMeta = (role: Role): { label: string; icon: string } => {
  return (getRoleMeta ? getRoleMeta(role) : null) ?? { label: role, icon: '' };
};

const getSubmarinePower = (submarine: SubmarineState): number => {
  return submarine.batteryCharge ?? 0;
};

const resolveActiveMap = () => getActiveMap?.() ?? null;

const harpoonRoomController: HarpoonRoomController = createHarpoonRoomController({
  controls,
  emitControl,
  getRenderTuning: () => controllerRenderTuning,
  getSubmarinePower,
  getGraphicsQuality: () => graphicsQuality,
  getFpsOverride: () => userFpsOverride
});

const tryHarpoonAction = (): boolean => harpoonRoomController.tryAction();
const tryHarpoonScan = (): boolean => harpoonRoomController.tryScan();
const bindHarpoonUiElements = (
  actionButton: HTMLButtonElement | null,
  scanButton: HTMLButtonElement | null,
  reloadProgressBar: HTMLDivElement | null,
  reloadStatusLabel: HTMLDivElement | null
): void => harpoonRoomController.bindUiElements(
  actionButton,
  scanButton,
  reloadProgressBar,
  reloadStatusLabel
);
const clearHarpoonUiElements = (): void => harpoonRoomController.clearUiElements();

const renderHarpoonControls = (): void => harpoonRoomController.render();

const weaponsRoomController: WeaponsRoomController = createWeaponsRoomController({
  controls,
  emitControl,
  getRenderTuning: () => controllerRenderTuning,
  getSubmarinePower,
  getGraphicsQuality: () => graphicsQuality,
  getFpsOverride: () => userFpsOverride
});
const renderWeaponsControls = (): void => weaponsRoomController.render('fore');
const renderWeaponsAftControls = (): void => weaponsRoomController.render('aft');

const engineeringRoomController = createEngineeringRoomController({
  controls,
  resolveRoleMeta,
  getControllerNetwork: () => controllerNetwork
});

const pilotRoomController = createPilotRoomController({
  controls,
  leftBottom: ctrlZoneLeftBottom,
  rightAux: ctrlZoneRightAux,
  emitControl
});

const sonarRoomController = createSonarRoomController({
  controls,
  getGraphicsQuality: () => graphicsQuality
});

const captainRoomController = createCaptainRoomController({
  controls,
  ctrlZoneLeftBottom,
  ctrlZoneRightAux,
  emitControl,
  resolveRoleMeta,
  resolveActiveMap,
  getControllerNetwork: () => controllerNetwork,
  requestSaveGame: () => saveGameWithFeedback(getRevealedNodes)
});

const dockingRoomController = createDockingRoomController({
  resolveActiveMap,
  getDockingCameraOriginMapPoint: (snapshot) => getDockingCameraOriginMapPoint(snapshot)
});

const batteryRoomController = createBatteryRoomController({
  controls,
  ctrlZoneLeftBottom,
  ctrlZoneRightAux,
  emitControl
});

const engineRoomController = createEngineRoomController({
  controls,
  ctrlZoneLeftBottom,
  ctrlZoneRightAux,
  emitControl
});

const lifeSupportRoomController = createLifeSupportRoomController({
  controls,
  ctrlZoneLeftBottom,
  ctrlZoneRightAux,
  emitControl,
  formatConsoleOutput: (snapshot) => {
    const crewCount = controllerState.lastRoomState?.players.filter((p) => p.role !== null).length ?? 0;
    const head = controllerState.lastRoomState?.craftQueue?.[0];
    const processorActive = (head?.remainingMs ?? 0) > 0 && snapshot.submarine.roomPower?.storage !== false;
    const processorLoad = processorActive ? (head?.isUpgrade ? UPGRADE_CRAFT_LOAD_MULTIPLIER : 1) : 0;
    return formatLifeSupportConsoleOutput(snapshot, crewCount, processorLoad);
  }
});

const droneRoomController = createDroneRoomController({
  controls,
  leftZone: ctrlZoneLeftBottom,
  rightZone: ctrlZoneRightAux,
  emitControl,
  getRenderTuning: () => controllerRenderTuning,
  getGraphicsQuality: () => graphicsQuality,
  getFpsOverride: () => userFpsOverride
});

const disposeDroneControls = (): void => droneRoomController.dispose();
export const disposeWeaponsControls = (): void => weaponsRoomController.dispose();

const commsRoomController = createCommsRoomController({ controls, emitControl });

const navigationRoomController = createNavigationRoomController({
  controls,
  emitControl,
  getRevealedNodes: () => revealedNodeIds,
  onWaypointChange: (pos) => {
    if (!pos) {
      controllerNetwork?.setNavWaypoint(null);
      return;
    }
    const simX = pos.worldX / SPATIAL_SCALE.mapXPerSimulationX;
    const simZ = pos.worldZ / SPATIAL_SCALE.mapZPerSimulationY;
    controllerNetwork?.setNavWaypoint({ simulationX: simX, simulationZ: simZ, ...(pos.depth !== undefined ? { depth: pos.depth } : {}) });
  }
});

const periscopeRoomController = createPeriscopeRoomController({
  controls,
  emitControl,
  getGraphicsQuality: () => graphicsQuality,
  getRenderTuning: () => controllerRenderTuning,
  onSnapshotPredicted: (snapshot) => updatePowerAndLightUi(snapshot)
});

const ballastRoomController = createBallastRoomController({
  controls,
  leftBottom: ctrlZoneLeftBottom,
  rightAux: ctrlZoneRightAux,
  onEmit: (action, value) => emitControl({ action, value } as ControlInput)
});

const storageRoomController = createStorageRoomController({
  controls,
  leftBottom: ctrlZoneLeftBottom,
  rightAux: ctrlZoneRightAux,
  getControllerNetwork: () => controllerNetwork
});

/** The submarine in the nav chart's reveal space: map x / z and layered local Y (depth = -(baseDepth + y)). */
const subRevealPoint = (snapshot: GameSnapshot): Point3 => {
  const baseDepth = (getActiveMap?.() ?? currentTileJsonMapDef)?.baseDepth ?? 0;
  return {
    x: snapshot.submarine.simulationX * SPATIAL_SCALE.mapXPerSimulationX,
    z: snapshot.submarine.simulationZ * SPATIAL_SCALE.mapZPerSimulationY,
    y: -snapshot.submarine.depth - baseDepth
  };
};

/** Chart graph (nodes + passage centre lines) the reveal rules run on; rebuilt whenever tile data changes. */
let revealGraph: RevealGraph | null = null;
/** Where the visual reveal last ran, so it is skipped until the sub has moved. */
let lastVisualRevealAt: Point3 | null = null;

/**
 * Called when a sonar ping completes (every phone receives the pulse). What each scan type uncovers is defined by the rules in
 * src/shared/maps/navReveal.ts: terrain = what the rays hit, subsurface = everything within range through rock, resource = nothing
 * on the chart (deposits are marked by the server). `rangeMeters` is the ping range in metres, which equal map units.
 */
const appendCaptainNavigationHits = (hits: SonarPingHit[], snapshot: GameSnapshot, rangeMeters?: number, scanType: ScanType = 'terrain'): void => {
  if (loadedTileJsons.size === 0 || !revealGraph) return;
  const baseDepth = (getActiveMap?.() ?? currentTileJsonMapDef)?.baseDepth ?? 0;
  const reveal = computeScanReveal({
    graph: revealGraph,
    scanType,
    origin: subRevealPoint(snapshot),
    range: rangeMeters ?? getSonarRangeMeters(),
    // Ray hits are Three.js world points (y = -depth); the chart uses layered local Y.
    hits: hits.filter((hit) => !hit.resourceType).map((hit) => ({ x: hit.x, y: hit.y - baseDepth, z: hit.z }))
  });
  if (applyReveal(revealedNodeIds, reveal)) navigationRoomController.syncRevealedNodes();
};

/** Reset node reveal state (call when mission or map changes). */
const resetNodeRevealState = (): void => {
  revealedNodeIds = new Set();
  lastVisualRevealAt = null;
  appliedNavRoutes.clear();
  navigationRoomController.syncRevealedNodes();
};

/**
 * The submarine's own presence uncovers the chamber it is in and the stretch of passage around it (navReveal.computeVisualReveal).
 * Reads loadedTileJsons, writes revealedNodeIds, then syncs the nav view. Safe to call on every snapshot tick.
 */
const checkVisualRevealForSnapshot = (snapshot: GameSnapshot): void => {
  const subWX = snapshot.submarine.simulationX * SPATIAL_SCALE.mapXPerSimulationX;
  const subWZ = snapshot.submarine.simulationZ * SPATIAL_SCALE.mapZPerSimulationY;
  // Safety net: when a room state was superseded while the map was still loading (a rejoin mid-game), the mission-change
  // hook never ran and the nav chart would stay empty. Start the tile JSON load ourselves once the active map is known.
  const activeMapNow = getActiveMap?.() ?? null;
  if (activeMapNow && currentTileJsonMapDef?.id !== activeMapNow.id && tileJsonBootstrapMapId !== activeMapNow.id) {
    tileJsonBootstrapMapId = activeMapNow.id;
    void loadTileJsonForMap(activeMapNow);
  }
  tryLoadAdjacentTileJsons(subWX, subWZ, -snapshot.submarine.depth);
  if (loadedTileJsons.size === 0 || !revealGraph) return;
  const sub = subRevealPoint(snapshot);
  if (lastVisualRevealAt && Math.hypot(sub.x - lastVisualRevealAt.x, sub.y - lastVisualRevealAt.y, sub.z - lastVisualRevealAt.z) < VISUAL_REVEAL_MIN_MOVE_METERS) return;
  lastVisualRevealAt = sub;
  if (applyReveal(revealedNodeIds, computeVisualReveal(revealGraph, sub), VISUAL_DISC_GRID_METERS)) navigationRoomController.syncRevealedNodes();
};

/**
 * Routes unlocked by the `unlockNavRoute` mission action (snapshot.navRoutes): a sub-surface scan from every route point charts the
 * chambers and passages along it. Re-run when more tile data has loaded, since the route may lie in tiles that were not loaded yet.
 */
const appliedNavRoutes = new Set<string>();
const applyNavRoutes = (snapshot: GameSnapshot): void => {
  const routes = snapshot.navRoutes;
  if (!routes?.length || loadedTileJsons.size === 0 || !revealGraph) return;
  const baseDepth = (getActiveMap?.() ?? currentTileJsonMapDef)?.baseDepth ?? 0;
  for (const route of routes) {
    const key = `${route.id}:${loadedTileJsons.size}`;
    if (appliedNavRoutes.has(key)) continue;
    appliedNavRoutes.add(key);
    let changed = false;
    for (const point of route.points) {
      const reveal = computeScanReveal({ graph: revealGraph, scanType: 'subsurface', origin: { x: point.x, z: point.z, y: -point.depth - baseDepth }, range: route.radius });
      changed = applyReveal(revealedNodeIds, reveal) || changed;
    }
    if (changed) navigationRoomController.syncRevealedNodes();
  }
};

/** Restore node reveal state from a serialised JSON string (from save file). */
const restoreNodeRevealState = (serialised: string): void => {
  try {
    revealedNodeIds = new Set(JSON.parse(serialised) as string[]);
  } catch {
    revealedNodeIds = new Set();
  }
  lastVisualRevealAt = null;
  navigationRoomController.syncRevealedNodes();
};

const getRevealedNodes = (): Set<string> => revealedNodeIds;

/**
 * Fetch and parse the companion JSON file(s) for the current map, then
 * aggregate connections and push node + connection data to the nav view.
 *
 * Coordinate system note:
 * node.x/z in chunk JSON are in GLB-local space. The server's mapManager
 * shifts every tile's collision mesh by:
 *   originX = tileX * tileSize.width - gridOriginOffset.x
 * where gridOriginOffset centres the whole tile grid. Submarine simulationX
 * is derived from positions already in this shifted world, so we must apply
 * the same shift (minus sourceGlbCenter, which handles mesh-centering) to
 * node positions here so sub and node circles are in the same coordinate frame.
 */
/** Rebuild allTileWorldOrigins from the map definition (call once on map load). */
const buildAllTileWorldOrigins = (mapDef: import('../shared/maps/types.js').MapDefinition): void => {
  allTileWorldOrigins.clear();
  for (const [tileId, origin] of computeTileOrigins(mapDef)) allTileWorldOrigins.set(tileId, origin);
};

/** Rebuild nav data from currently loaded tiles and push to the navigation controller. */
const refreshNavData = (): void => {
  const mapDef = currentTileJsonMapDef;

  tileCenterOffsets.clear();
  for (const [tileId] of loadedTileJsons) {
    tileCenterOffsets.set(tileId, allTileWorldOrigins.get(tileId) ?? { x: 0, z: 0 });
  }

  const tileOffsets = new Map<string, { x: number; z: number; y?: number }>(
    [...allTileWorldOrigins.entries()].filter(([tileId]) => loadedTileJsons.has(tileId))
  );
  // Single-tile maps with a top-level sourceGlbFile
  if (mapDef?.sourceGlbFile && (mapDef.sourceGlbTiles ?? []).length === 0 && loadedTileJsons.size === 1) {
    const tj = [...loadedTileJsons.values()][0];
    const cx = mapDef.sourceGlbCenter?.x ?? 0;
    const cz = mapDef.sourceGlbCenter?.z ?? 0;
    const origin = { x: -cx, z: -cz };
    tileCenterOffsets.set(tj.metadata.tile_id, origin);
    tileOffsets.set(tj.metadata.tile_id, origin);
  }

  const { nodeEntries, connections, gateways } = buildNavData(loadedTileJsons, tileOffsets);
  revealGraph = buildRevealGraph(nodeEntries, connections);
  lastVisualRevealAt = null;
  navigationRoomController.setTileData(nodeEntries, connections, gateways);
};

/**
 * Fetch a single tile's companion JSON and integrate it. No-ops if already loaded or pending.
 * Rebuilds nav data after load.
 */
const fetchAndIntegrateTileJson = async (glbPath: string, tileKey: string = tileKeyForGlb(glbPath)): Promise<void> => {
  if (loadedTileGlbFiles.has(tileKey) || pendingTileJsonFetches.has(tileKey)) return;
  pendingTileJsonFetches.add(tileKey);
  const jsonPath = `/maps/${glbPath.replace(/\.glb$/i, '.json')}`;
  try {
    const res = await fetch(jsonPath);
    if (res.ok) {
      const data = await res.json() as TileJson;
      tileKey = tileKey || data.metadata.tile_id;
      loadedTileJsons.set(tileKey, data);
      loadedTileGlbFiles.add(tileKey);
      refreshNavData();
      if (controllerState.lastSnapshot) checkVisualRevealForSnapshot(controllerState.lastSnapshot);
    }
  } catch {
    // No companion JSON for this tile — silently skip
  } finally {
    pendingTileJsonFetches.delete(tileKey);
  }
};

const loadTileJsonForMap = async (mapDef: import('../shared/maps/types.js').MapDefinition): Promise<void> => {
  loadedTileJsons.clear();
  loadedTileGlbFiles.clear();
  tileCenterOffsets.clear();
  allTileWorldOrigins.clear();
  pendingTileJsonFetches.clear();
  currentTileJsonMapDef = mapDef;

  buildAllTileWorldOrigins(mapDef);

  const sourceTiles = mapDef.sourceGlbTiles ?? [];

  if (sourceTiles.length > 0) {
    // Multi-tile map: only load the spawn tile's JSON to keep startup memory low.
    // Adjacent tiles load lazily via tryLoadAdjacentTileJsons as the sub moves.
    const tileSize = mapDef.sourceGlbTileSizeMeters ?? { X: 0, Y: 0, Z: 0 };
    const gridOriginOffset = getSourceGlbTileGridOffset(sourceTiles, tileSize, mapDef.sourceGlbGridBounds);
    const spawn = mapDef.missionLayout?.submarineSpawn;
    let spawnTile = sourceTiles[0];
    if (spawn && typeof spawn.x === 'number' && typeof spawn.z === 'number') {
      const spawnTileX = Math.floor((spawn.x + gridOriginOffset.x) / tileSize.X);
      const spawnTileZ = Math.floor((spawn.z + gridOriginOffset.z) / tileSize.Z);
      // Stacked columns: take the layer whose slab holds the spawn depth (a deep chapter spawns many layers down).
      const column = sourceTiles.filter(t => t.tileX === spawnTileX && t.tileZ === spawnTileZ);
      const spawnY = typeof spawn.depth === 'number' ? -spawn.depth : undefined;
      spawnTile = (spawnY !== undefined
        ? [...column].sort((a, b) => verticalGap(...slabBounds(mapDef, a), spawnY) - verticalGap(...slabBounds(mapDef, b), spawnY))[0]
        : column[0]) ?? sourceTiles[0];
    } else if (spawn && typeof spawn.tileX === 'number' && typeof spawn.tileZ === 'number') {
      spawnTile = sourceTiles.find(t => t.tileX === spawn.tileX && t.tileZ === spawn.tileZ && (spawn.tileY === undefined || t.tileY === spawn.tileY))
        ?? sourceTiles.find(t => t.tileX === spawn.tileX && t.tileZ === spawn.tileZ) ?? sourceTiles[0];
    }
    await fetchAndIntegrateTileJson(spawnTile.sourceGlbFile, tileKeyForSlot(spawnTile, sourceTiles));
  } else if (mapDef.sourceGlbFile) {
    // Single-tile map: load the one companion JSON
    const jsonPath = `/maps/${mapDef.sourceGlbFile.replace(/\.glb$/i, '.json')}`;
    try {
      const res = await fetch(jsonPath);
      if (res.ok) {
        const data = await res.json() as TileJson;
        loadedTileJsons.set(data.metadata.tile_id, data);
      }
    } catch {
      // No companion JSON
    }
    refreshNavData();
  }

  if (controllerState.lastSnapshot) checkVisualRevealForSnapshot(controllerState.lastSnapshot);
};

/**
 * Called on each snapshot tick. Loads tile JSONs for tiles the sub is approaching
 * (within one tile-width of the tile center) so nav data is ready before the sub arrives.
 */
/** World-Y slab [minY, maxY] of a tile's layer (tile index when the map has one, else derived from the base depth). */
const slabIndexCache = new WeakMap<object, Map<string, [number, number]>>();
const slabBounds = (mapDef: import('../shared/maps/types.js').MapDefinition, td: { tileX: number; tileY: number; tileZ: number }): [number, number] => {
  let byKey = slabIndexCache.get(mapDef);
  if (!byKey) {
    byKey = new Map();
    for (const e of mapDef.tileIndex ?? []) if (e.minY !== undefined && e.maxY !== undefined) byKey.set(`${e.tileX}_${e.tileY}_${e.tileZ}`, [e.minY, e.maxY]);
    slabIndexCache.set(mapDef, byKey);
  }
  const known = byKey.get(`${td.tileX}_${td.tileY}_${td.tileZ}`);
  if (known) return known;
  const sizeY = mapDef.sourceGlbTileSizeMeters?.Y ?? 0;
  const centre = (mapDef.baseDepth ?? 0) + td.tileY * sizeY;
  return [centre - sizeY / 2, centre + sizeY / 2];
};

const tryLoadAdjacentTileJsons = (subWorldX: number, subWorldZ: number, subWorldY?: number): void => {
  const mapDef = currentTileJsonMapDef;
  if (!mapDef) return;
  const sourceTiles = mapDef.sourceGlbTiles ?? [];
  if (sourceTiles.length === 0) return;
  const tileSize = mapDef.sourceGlbTileSizeMeters ?? { X: 0, Y: 0, Z: 0 };
  const gridOriginOffset = getSourceGlbTileGridOffset(sourceTiles, tileSize, mapDef.sourceGlbGridBounds);
  // Load a tile when the sub is within one tile-width of its center (gives a ~150m early-load margin)
  const loadRadius = Math.max(tileSize.X, tileSize.Z);

  for (const td of sourceTiles) {
    const edgeX = td.tileX * tileSize.X - gridOriginOffset.x;
    const edgeZ = td.tileZ * tileSize.Z - gridOriginOffset.z;
    const centerX = edgeX + tileSize.X / 2;
    const centerZ = edgeZ + tileSize.Z / 2;
    const dx = subWorldX - centerX;
    const dz = subWorldZ - centerZ;
    if (dx * dx + dz * dz > loadRadius * loadRadius) continue;
    // Stacked layers: only the layers near the sub's depth (the one it is in and its neighbours), not a whole shaft.
    if (subWorldY !== undefined && verticalGap(...slabBounds(mapDef, td), subWorldY) > tileSize.Y) continue;
    // Tile is in range — load its JSON if not already loaded
    const slotKey = tileKeyForSlot(td, sourceTiles);
    if (!loadedTileGlbFiles.has(slotKey)) {
      void fetchAndIntegrateTileJson(td.sourceGlbFile, slotKey);
    }
  }
};

const describeDockingApproach = (snapshot: GameSnapshot) =>
  dockingRoomController.describeApproach(snapshot);

const updateCaptainPanel = (snapshot: GameSnapshot | null): void => {
  captainRoomController.updatePanel(snapshot);
};

const updateCaptainRoleAssignments = (): void => {
  captainRoomController.updateRoleAssignments();
};

const getSonarScanType = (): SonarScanType => sonarRoomController.getScanType();

const captureSonarPingHitsForNavigation = (snapshot: GameSnapshot, scanType?: SonarScanType): SonarPingHit[] => {
  return sonarRoomController.capturePingHitsForNavigation(snapshot, scanType);
};

const updateSonarDepthDial = (snapshot: GameSnapshot | null): void => {
  sonarRoomController.updateDepthDial(snapshot);
};

const setSonarRoomState = (roomState: RoomState): void => {
  sonarRoomController.setRoomState(roomState);
};

let revealedNodeIds: Set<string> = new Set();
const loadedTileJsons = new Map<string, TileJson>();
/** GLB-local → world/map center offsets per tile_id (populated by loadTileJsonForMap). */
const tileCenterOffsets = new Map<string, { x: number; z: number }>();
/** World-space center offsets for ALL tiles (loaded or not), keyed by sourceGlbFile basename. */
const allTileWorldOrigins = new Map<string, { x: number; z: number; y: number }>();
/** The map definition currently in use for tile JSON loading. */
let currentTileJsonMapDef: import('../shared/maps/types.js').MapDefinition | null = null;
/** Map id the nav chart's fallback tile JSON load was last started for (see checkVisualRevealForSnapshot). */
let tileJsonBootstrapMapId: string | null = null;
/** GLB files whose JSON is currently being fetched (prevents duplicate requests). */
const pendingTileJsonFetches = new Set<string>();
/** GLB file paths whose companion JSON has already been loaded into loadedTileJsons. */
const loadedTileGlbFiles = new Set<string>();
/** Metres the sub must move before the visual reveal runs again. */
const VISUAL_REVEAL_MIN_MOVE_METERS = 2;

const updateSonarBatteryWarning = (snapshot: GameSnapshot | null): void => {
  sonarRoomController.updateBatteryWarning(snapshot);
};

const setSonarRangeUpdateCallback = (callback: (() => void) | null): void => {
  sonarRoomController.setRangeUpdateCallback(callback);
};

export const incrementSonarRange = (): void => {
  sonarRoomController.incrementRange();
};

export const decrementSonarRange = (): void => {
  sonarRoomController.decrementRange();
};

let bottomHullCameraView: BottomHullCameraView | null = null;

let harpoonLightsButton: HTMLButtonElement | null = null;

let engineSlider: HTMLInputElement | null = null;
let engineResetButton: HTMLButtonElement | null = null;
let dockingLightsButton: HTMLButtonElement | null = null;
let dockedToggleButton: HTMLButtonElement | null = null;
let dockingDistanceLabel: HTMLElement | null = null;
let dockingViewportElement: HTMLElement | null = null;

const renderStorageControls = (): void => {
  storageRoomController.render();
};

const handleRoleTap = (role: Role, roomState: RoomState): void => {
  if (!controllerState.currentRoomCode) {
    showControllerQuickToast('Join a room first.');
    return;
  }

  if (controllerState.currentRole === role) {
    return;
  }

  const available = roomState.availableRoles.includes(role);
  if (!available) {
    showControllerQuickToast('Role occupied.');
    return;
  }

  controllerNetwork?.chooseRole(role);
};

const createRoleButton = (role: Role, roomState: RoomState, _compact: boolean): HTMLButtonElement => {
  const button = document.createElement('button');
  const available = roomState.availableRoles.includes(role) || role === controllerState.currentRole;
  const meta = resolveRoleMeta(role);

  button.type = 'button';
  button.title = meta.label;
  button.setAttribute('aria-label', meta.label);
  button.className = 'role-slot-button';
  button.classList.toggle('active', controllerState.currentRole === role);
  button.classList.toggle('taken', !available && role !== controllerState.currentRole);
  button.innerHTML = `<span class="role-icon">${meta.icon}</span><span class="role-slot-label">${meta.label}</span>`;

  button.addEventListener('click', () => {
    handleRoleTap(role, roomState);
  });

  return button;
};

const updatePilotPanel = (snapshot: GameSnapshot | null): void => {
  pilotRoomController.updatePanel(snapshot);
};

const updateBallastPanel = (snapshot: GameSnapshot | null): void => {
  ballastRoomController.updatePanel(snapshot);
};

const renderPilotControls = (): void => {
  pilotRoomController.render();
};

const renderSonarControls = (): void => {
  sonarRoomController.render();
};

const getSonarRangeMeters = (): number => sonarRoomController.getRangeMeters();

const createPeriscopeLightsButton = (): HTMLButtonElement => {
  return periscopeRoomController.createLightsButton();
};

const renderBallastControls = (): void => {
  ballastRoomController.render();
};

const renderEngineerControls = (): void => {
  engineeringRoomController.render();
};

export const updateEngineerPanel = (snapshot: GameSnapshot | null): void => {
  engineeringRoomController.update(snapshot);
};

export const disposeEngineerView = (): void => {
  engineeringRoomController.dispose();
};

let roomPowerOffOverlay: HTMLDivElement | null = null;

const ensureRoomPowerOffOverlay = (): HTMLDivElement => {
  if (!roomPowerOffOverlay) {
    const overlay = document.createElement('div');
    overlay.className = 'room-power-off-overlay';
    overlay.style.display = 'none';
    overlay.innerHTML = '<div class="room-power-off-overlay-inner">'
      + '<span class="room-power-off-overlay-title">POWER OFF</span>'
      + '<span class="room-power-off-overlay-sub">No power to this room</span>'
      + '<button type="button" class="room-power-off-overlay-back">Back to rooms</button>'
      + '</div>';
    // The overlay covers the whole screen (blocking the header room menu), so it
    // needs its own way out — open the room selector via the existing button.
    overlay.querySelector('.room-power-off-overlay-back')?.addEventListener('click', () => {
      document.querySelector<HTMLButtonElement>('#roomMenuButton')?.click();
    });
    document.body.appendChild(overlay);
    roomPowerOffOverlay = overlay;
  }
  return roomPowerOffOverlay;
};

const updateRoomPowerOffOverlay = (snapshot: GameSnapshot | null): void => {
  const overlay = ensureRoomPowerOffOverlay();
  const role = controllerState.currentRole;
  const off = !!role && snapshot?.submarine.roomPower?.[role] === false;
  overlay.style.display = off ? 'flex' : 'none';
};

/**
 * controller.ts builds the docking room's DOM, but the per-snapshot update runs in
 * this module against its own element references — which were never assigned, so
 * the Dock/Undock button, lights label and distance readout only ever reflected
 * the snapshot present at render time (null when entering the room right after
 * game start, hence a wrong "Dock" until the room was re-entered).
 */
const setDockingUiElements = (elements: {
  dockingLightsButton: HTMLButtonElement | null;
  dockedToggleButton: HTMLButtonElement | null;
  dockingDistanceLabel: HTMLElement | null;
  dockingViewportElement: HTMLElement | null;
}): void => {
  dockingLightsButton = elements.dockingLightsButton;
  dockedToggleButton = elements.dockedToggleButton;
  dockingDistanceLabel = elements.dockingDistanceLabel;
  dockingViewportElement = elements.dockingViewportElement;
};

const updatePowerAndLightUi = (snapshot: GameSnapshot | null): void => {
  if (!snapshot) {
    return;
  }

  updateRoomPowerOffOverlay(snapshot);
  updateSonarBatteryWarning(snapshot);
  batteryRoomController.updateUi(snapshot);

  engineRoomController.updateUi(snapshot);
  captainRoomController.updateActionButtons(snapshot);
  dockingRoomController.updateUi(snapshot, {
    dockingLightsButton,
    dockedToggleButton,
    dockingDistanceLabel,
    dockingViewportElement
  });
  if (harpoonLightsButton) {
    const button = harpoonLightsButton as HTMLButtonElement;
    button.textContent = snapshot.submarine.miningLightsOn ? 'Turn mining lights OFF' : 'Turn mining lights ON';
  }
  periscopeRoomController.updateLightsButton(snapshot.submarine.periscopeLightOn);
  lifeSupportRoomController.updateUi(snapshot);
};

const stopBatteryAnimation = (): void => {
  batteryRoomController.stopAnimation();
};

const renderBatteryControls = (): void => {
  batteryRoomController.render();
  batteryRoomController.updateUi(controllerState.lastSnapshot);
};

const renderEngineControls = (): void => {
  engineRoomController.render();
};

const renderLifeSupportControls = (): void => {
  lifeSupportRoomController.render();
};

const renderDroneControls = (): void => {
  droneRoomController.render();
};

const renderNavigationControls = (): void => {
  navigationRoomController.render(controllerState.lastRoomState, controllerState.lastSnapshot);
};

const renderCommsControls = (): void => {
  commsRoomController.render(controllerState.lastSnapshot);
};

const renderCaptainControls = (): void => {
  captainRoomController.render();
};

const renderPeriscopeControls = (): void => {
  periscopeRoomController.render();
};

const getDockingCameraOriginMapPoint = (snapshot: GameSnapshot): { x: number; y: number; z: number } => {
  const subPoint = submarineToMapPoint(snapshot.submarine);
  return {
    x: subPoint.x + controllerRenderTuning.hullCameraPosition.x,
    y: -snapshot.submarine.depth + controllerRenderTuning.hullCameraPosition.y,
    z: subPoint.z + controllerRenderTuning.hullCameraPosition.z
  };
};

export {
  createRoleButton,
  handleRoleTap,
  renderStorageControls,
  renderPilotControls,
  renderSonarControls,
  renderBallastControls,
  renderHarpoonControls,
  renderWeaponsControls,
  renderWeaponsAftControls,
  renderPeriscopeControls,
  renderEngineerControls,
  renderBatteryControls,
  renderEngineControls,
  renderLifeSupportControls,
  renderDroneControls,
  renderNavigationControls,
  renderCommsControls,
  renderCaptainControls,
  tryHarpoonAction,
  tryHarpoonScan,
  updatePowerAndLightUi,
  setDockingUiElements,
  updatePilotPanel,
  updateBallastPanel,
  updateCaptainPanel,
  updateCaptainRoleAssignments,
  updateSonarDepthDial,
  getSonarScanType,
  appendCaptainNavigationHits,
  captureSonarPingHitsForNavigation,
  stopBatteryAnimation,
  getDockingCameraOriginMapPoint,
  describeDockingApproach,
  getRevealedNodes,
  resetNodeRevealState,
  restoreNodeRevealState,
  checkVisualRevealForSnapshot,
  loadTileJsonForMap,
  getSonarRangeMeters,
  getSonarPulsePowerMw,
  setSonarRangeUpdateCallback,
  createPeriscopeLightsButton,
  setSonarRoomState,
  bindHarpoonUiElements,
  clearHarpoonUiElements,
  disposeDroneControls
};

